import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionStepValueType } from '@core/models/built/session-step-value-type.enum';
import { SessionStep, SessionStepValue } from '@core/models/session.model';
import { AliaBackendService } from '@customization/alia-backend.service';
import { DeliveryPoint } from '@customization/models/delivery-point.model';
import { Department } from '@customization/models/department.model';
import { Employee } from '@customization/models/employee.model';
import { Gender } from '@customization/models/gender.enum';
import {
  EmployeeType,
  ManageableFields,
  ManageEmployeeConfig,
} from '@customization/models/manage-employee-config.model';
import { ManageEmployeeJson } from '@customization/models/manage-employee-json.model';
import { PurposeNodeGate } from '@customization/models/purpose.model';
import { TranslocoService } from '@ngneat/transloco';
import { conditionalValidator, filterTruthy } from '@utils';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { concatMap, first, map } from 'rxjs/operators';

import it from './i18n/manage-employee-session.it.json';

@Component({
  templateUrl: './manage-employee-session.component.html',
  styles: [
  ]
})
export class ManageEmployeeSessionComponent implements OnInit, OnChanges, OnDestroy {

  @Input() step!: SessionStep;

  @Output() commitAndNext = new EventEmitter<SessionStep>();

  private step$$ = new BehaviorSubject<SessionStep>(this.step);
  step$ = this.step$$.pipe(filterTruthy());

  config$ = this.step$.pipe(map(step => step.procedureStep?.config as ManageEmployeeConfig));

  manageableFields$ = this.config$.pipe(
    map(config => config.manageableFields),
    filterTruthy()
  );

  showRegistrationNumber$ = this.manageableFields$.pipe(
    map(mf => mf.includes(ManageableFields.registrationNumber))
  );
  showDeliveryPoint$ = this.manageableFields$.pipe(
    map(mf => mf.includes(ManageableFields.deliveryPoint))
  );
  showDepartment$ = this.manageableFields$.pipe(
    map(mf => mf.includes(ManageableFields.department))
  );
  showPurpose$ = this.manageableFields$.pipe(
    map(mf => mf.includes(ManageableFields.purpose))
  );
  showContract$ = this.manageableFields$.pipe(
    map(mf => mf.includes(ManageableFields.contract))
  );
  showMeasures$ = this.manageableFields$.pipe(
    map(mf => mf.includes(ManageableFields.measures))
  );

  private employees$$ = new BehaviorSubject<Employee[]>([]);
  employees$ = this.employees$$.asObservable();

  private purposes$$ = new BehaviorSubject<PurposeNodeGate[]>([]);
  purposes$ = this.purposes$$.asObservable();

  private deliveryPoint$$ = new BehaviorSubject<DeliveryPoint[]>([]);
  deliveryPoint$ = this.deliveryPoint$$.asObservable();

  private department$$ = new BehaviorSubject<(Department | Partial<Department>)[]>([]);
  department$ = this.department$$.asObservable();

  employeeSearchFn = (name?: string) => this.employees$.pipe(
    map(ee => name ? ee.filter(e => this.employeeLabelFn(e).toLowerCase().indexOf(name.toLowerCase()) > -1) : ee)
  );
  employeeLabelFn = (e: Employee) => `${e.lastname} ${e.firstname}`;

  purposeSearchFn = (name?: string) => this.purposes$.pipe(
    map(pp => name ? pp.filter(p => this.purposeLabelFn(p).toLowerCase().indexOf(name.toLowerCase()) > -1) : pp)
  );
  purposeLabelFn = (p: Partial<PurposeNodeGate>) => `${p.CODICE} - ${p.DESCRIZIONE} ${p.SESSO ? '(' + p.SESSO + ')' : ''}`;

  deliveryPointSearchFn = (name?: string) => this.deliveryPoint$$.pipe(
    map(dps => name ? dps.filter(dp => this.deliveryPointLabelFn(dp).toLowerCase().indexOf(name.toLowerCase()) > -1) : dps)
  );
  deliveryPointLabelFn = (dp: Partial<DeliveryPoint>) => `${dp.CODICE_PUNTO_CONSEGNA} - ${dp.DESCRIZIONE}`;

  departmentSearchFn = (name?: string) => this.department$.pipe(
    map(dd => name ? dd.filter(d => this.departmentLabelFn(d).toLowerCase().indexOf(name.toLowerCase()) > -1) : dd)
  );
  departmentLabelFn = (d: Department | Partial<Department>) => `${d.CODICE_REPARTO} - ${d.DESCRIZIONE}`;

  inputForm$ = this.config$.pipe(
    map(({ manageableFields }) => {
      const predicate = (mf: ManageableFields) => () => manageableFields && manageableFields.includes(mf);
      const form = this.fb.group({
        employee: [undefined, [Validators.required]],
        registrationNumber: [undefined, [Validators.required]],
        contractStartOn: [undefined, conditionalValidator(predicate(ManageableFields.contract), Validators.required)],
        contractEndOn: [undefined, conditionalValidator(predicate(ManageableFields.contract), Validators.required)],
        deliveryPoint: [undefined, conditionalValidator(predicate(ManageableFields.deliveryPoint), Validators.required)],
        department: [undefined, conditionalValidator(predicate(ManageableFields.department), Validators.required)],
        chest: [undefined, conditionalValidator(predicate(ManageableFields.measures), Validators.required)],
        waist: [undefined, conditionalValidator(predicate(ManageableFields.measures), Validators.required)],
        hip: [undefined, conditionalValidator(predicate(ManageableFields.measures), Validators.required)],
        pelvis: [undefined, conditionalValidator(predicate(ManageableFields.measures), Validators.required)],
        shoes: [undefined, conditionalValidator(predicate(ManageableFields.measures), Validators.required)],
        purpose: [undefined, conditionalValidator(predicate(ManageableFields.purpose), Validators.required)],
      });
      return form;
    })
  );

  private purposeLabel$$ = new BehaviorSubject<string>('');
  purposeLabel$ = this.purposeLabel$$.asObservable();

  private deliveryPointLabel$$ = new BehaviorSubject<string>('');
  deliveryPointLabel$ = this.deliveryPointLabel$$.asObservable();

  private departmentLabel$$ = new BehaviorSubject<string>('');
  departmentLabel$ = this.departmentLabel$$.asObservable();

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private translocoSvc: TranslocoService,
    private aliaSvc: AliaBackendService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {
    this.config$.pipe(
      concatMap(({ employeeType, manageableFields }) => {
        let employees$: Observable<Employee[]>;
        if (employeeType === EmployeeType.confirmed) {
          employees$ = this.aliaSvc.getConfirmedEmployees();
        } else {
          employees$ = this.aliaSvc.getNotConfirmedEmployees();
        }
        const purposes$ = manageableFields && manageableFields.includes(ManageableFields.purpose) ? this.aliaSvc.getPurpose() : of([]);
        const deliveryPoint$ = manageableFields && manageableFields.includes(ManageableFields.deliveryPoint) ? this.aliaSvc.getDeliveryPoint() : of([]);
        return combineLatest([
          employees$,
          purposes$,
          deliveryPoint$
        ]);
      }),
      first()
    ).subscribe(([employees, purposes, deliveryPoints]) => {
      this.employees$$.next(employees);
      this.purposes$$.next(purposes);
      this.deliveryPoint$$.next(deliveryPoints);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.step) {
      this.step$$.next(changes.step.currentValue);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  selectEmployee(e: Employee | null | string | number, form: FormGroup) {
    if (e) {
      const employee = e as Employee;
      const {
        chest,
        waist,
        hip,
        pelvis,
        shoes,
        contractStartOn,
        contractEndOn,
        deliveryPointCode,
        deliveryPointName,
        departmentCode,
        departmentName,
        purposeCode,
        purposeName,
        registrationNumber,
        gender
      } = employee;
      /* eslint-disable @typescript-eslint/naming-convention */
      const department: Partial<Department> | null = departmentCode || departmentName ? {
        CODICE_REPARTO: departmentCode,
        DESCRIZIONE: departmentName,
      } : null;

      const deliveryPoint: Partial<DeliveryPoint> | null = deliveryPointCode || deliveryPointName ? {
        CODICE_PUNTO_CONSEGNA: deliveryPointCode,
        DESCRIZIONE: deliveryPointName,
        REPARTI: department ? [department] : []
      } : null;
      const purpose: Partial<PurposeNodeGate> | null = purposeCode && purposeName && gender ? {
        CODICE: purposeCode,
        DESCRIZIONE: purposeName,
        SESSO: gender === Gender.M ? 'M' : 'F'
      } : null;
      /* eslint-enable @typescript-eslint/naming-convention */

      this.purposeLabel$$.next(purpose ? this.purposeLabelFn(purpose) : '');
      this.deliveryPointLabel$$.next(deliveryPoint ? this.deliveryPointLabelFn(deliveryPoint) : '');
      this.departmentLabel$$.next(department ? this.departmentLabelFn(department) : '');

      form.setValue({
        employee,
        deliveryPoint,
        department,
        purpose,
        chest,
        waist,
        hip,
        pelvis,
        shoes,
        contractStartOn,
        contractEndOn,
        registrationNumber
      });
    } else {
      form.reset();
      this.purposeLabel$$.next('');
      this.deliveryPointLabel$$.next('');
      this.departmentLabel$$.next('');
    }
  }

  selectDeliveryPoint(dp: DeliveryPoint | null | string | number) {
    let departments: (Department | Partial<Department>)[] = [];
    if (dp) {
      const deliveryPoint = dp as DeliveryPoint;
      departments = deliveryPoint.REPARTI;
    }
    this.department$$.next(departments);
  }

  submit(form: FormGroup, step: SessionStep) {

    if (form.valid) {
      const {
        employee,
        registrationNumber,
        deliveryPoint,
        department,
        purpose,
        contractStartOn,
        contractEndOn,
        chest,
        waist,
        hip,
        pelvis,
        shoes
      } = form.value;
      const e = (employee as Employee);
      const dp = (deliveryPoint as DeliveryPoint);
      const d = (department as Department);
      const p = (purpose as PurposeNodeGate);
      const json: ManageEmployeeJson = {
        input: {
          id: e.id,
          employeeFullname: `${e.lastname} ${e.firstname}`,
          employeeRegistrationNumber: registrationNumber,
          employeePhoneNr: e.mobilephone,
          deliveryPointCode: dp?.CODICE_PUNTO_CONSEGNA,
          deliveryPointName: dp?.DESCRIZIONE,
          departmentCode: d?.CODICE_REPARTO,
          departmentName: d?.DESCRIZIONE,
          referrerEmail: dp?.EMAIL,
          purposeCode: p?.CODICE,
          purposeName: p?.DESCRIZIONE,
          confirmedOn: DateTime.local().toJSDate(),
          contractStartOn,
          contractEndOn,
          chest,
          waist,
          hip,
          pelvis,
          shoes
        }
      };
      const now = DateTime.local();
      const value: SessionStepValue = {
        workflowId: step.workflowId,
        workflowStepId: step.workflowStepId,
        sessionId: step.sessionId,
        timestamp: now.toJSDate(),
        type: SessionStepValueType.inputObject,
        value: json
      };
      this.commitAndNext.emit({ ...step, completedOn: now.toJSDate(), values: [value] });
    }
  }

}
